import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useServiceOS } from '../service-os/hooks/useServiceOS';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { toast } from 'sonner';
import {
  Buildings, Clock, CurrencyDollar, BookOpen, Check,
  ArrowRight, ArrowLeft, CircleNotch, Sparkle,
  GlobeSimple, MagicWand, FloppyDisk, UploadSimple,
  File as FileIcon, Trash, DownloadSimple,
} from '@phosphor-icons/react';

const INDUSTRIES = [
  'Plumbing', 'Electrical', 'HVAC', 'Cleaning',
  'Landscaping', 'Painting', 'Roofing', 'Building & Construction',
  'Pest Control', 'Locksmith', 'Auto Repair', 'Beauty & Wellness',
  'Health & Allied Health', 'Legal Services', 'Accounting',
  'Real Estate', 'Photography', 'IT Services', 'Other',
];

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const STEPS = [
  { id: 'profile', label: 'Profile', icon: Buildings },
  { id: 'hours', label: 'Hours', icon: Clock },
  { id: 'pricing', label: 'Pricing', icon: CurrencyDollar },
  { id: 'knowledge', label: 'Knowledge', icon: BookOpen },
  { id: 'done', label: 'Done', icon: Check },
];

/* ── Shared input field ── */
const InputField = ({ label, value, onChange, placeholder, type = 'text', rows, testId }) => (
  <div>
    <label className="text-xs font-medium text-[#B0B8C4] block mb-1.5">{label}</label>
    {rows ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        data-testid={testId}
        className="w-full px-3 py-2.5 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm placeholder:text-[#4B5563] focus:outline-none focus:border-[#00E5C0] resize-none"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2.5 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm placeholder:text-[#4B5563] focus:outline-none focus:border-[#00E5C0]"
      />
    )}
  </div>
);

export default function BusinessSetupWizard() {
  const navigate = useNavigate();
  const { getProfile, patchProfile, completeOnboarding } = useServiceOS();
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Autofill state
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [autofilling, setAutofilling] = useState(false);
  const [autofillResult, setAutofillResult] = useState(null);

  // Knowledge state
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    business_name: '',
    industry: '',
    services_offered: '',
    working_hours: {
      mon: { enabled: true, start: '08:00', end: '17:00' },
      tue: { enabled: true, start: '08:00', end: '17:00' },
      wed: { enabled: true, start: '08:00', end: '17:00' },
      thu: { enabled: true, start: '08:00', end: '17:00' },
      fri: { enabled: true, start: '08:00', end: '17:00' },
      sat: { enabled: false, start: '08:00', end: '12:00' },
      sun: { enabled: false, start: '08:00', end: '12:00' },
    },
    buffer_between_jobs: '',
    pricing_notes: '',
    callout_fee: '',
    hourly_rate: '',
  });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Load existing profile data
  useEffect(() => {
    (async () => {
      try {
        const data = await getProfile();
        if (data?.profile) {
          const p = data.profile;
          setForm(prev => ({
            ...prev,
            business_name: p.business_name || prev.business_name,
            industry: p.industry || p.business_type || prev.industry,
            services_offered: p.services_offered || prev.services_offered,
            working_hours: p.working_hours || prev.working_hours,
            buffer_between_jobs: p.buffer_between_jobs || p.buffer_minutes || prev.buffer_between_jobs,
            pricing_notes: p.pricing_notes || prev.pricing_notes,
            callout_fee: p.callout_fee || p.callout_fee_text || prev.callout_fee,
            hourly_rate: p.hourly_rate || prev.hourly_rate,
          }));
          setWebsiteUrl(p.website_url || '');
          if (!p.working_hours && p.working_days) {
            const wh = {};
            for (const d of DAYS) {
              wh[d.key] = {
                enabled: p.working_days.includes(d.label),
                start: p.workday_start_time || '08:00',
                end: p.workday_end_time || '17:00',
              };
            }
            setForm(prev => ({ ...prev, working_hours: wh }));
          }
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  // Fetch knowledge documents
  const fetchDocs = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/knowledge`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch {}
  }, [orgId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const toggleDay = (dayKey) => {
    setForm(prev => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [dayKey]: { ...prev.working_hours[dayKey], enabled: !prev.working_hours[dayKey]?.enabled },
      },
    }));
  };

  const setDayTime = (dayKey, field, value) => {
    setForm(prev => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [dayKey]: { ...prev.working_hours[dayKey], [field]: value },
      },
    }));
  };

  /* ── Autofill handler ── */
  const handleAutofill = async () => {
    const url = websiteUrl.trim();
    if (!url) { toast.error('Enter your website URL first.'); return; }
    setAutofilling(true);
    setAutofillResult(null);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/profile/autofill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      }, orgId);
      if (res.ok) {
        const data = await res.json();
        const ex = data.extracted || {};
        setAutofillResult(ex);
        // Pre-fill ALL fields from extraction
        if (ex.business_name) set('business_name', ex.business_name);
        if (ex.industry) set('industry', ex.industry.toLowerCase());
        if (ex.services_offered) set('services_offered', ex.services_offered);
        if (ex.pricing_notes) set('pricing_notes', ex.pricing_notes);
        if (ex.callout_fee) set('callout_fee', ex.callout_fee);
        toast.success('Details extracted from your website! Review and continue.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not extract details from this website.');
      }
    } catch { toast.error('Could not reach the website.'); }
    setAutofilling(false);
  };

  /* ── Knowledge handlers ── */
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/knowledge`, {
        method: 'POST',
        body: formData,
      }, orgId);
      if (res.ok) {
        toast.success(`"${file.name}" uploaded.`);
        fetchDocs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed.');
      }
    } catch { toast.error('Upload failed.'); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteDoc = async (docId, filename) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/knowledge/${docId}`, {
        method: 'DELETE',
      }, orgId);
      if (res.ok) {
        toast.success(`"${filename}" removed.`);
        fetchDocs();
      }
    } catch {}
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canAdvance = () => {
    if (step === 0) return form.business_name.trim() && form.industry.trim() && form.services_offered.trim();
    if (step === 1) return Object.values(form.working_hours).some(d => d.enabled);
    return true;
  };

  const handleNext = async () => {
    if (step < 4) {
      setSaving(true);
      try {
        if (step === 0) {
          await patchProfile({
            business_name: form.business_name,
            industry: form.industry,
            business_type: form.industry,
            services_offered: form.services_offered,
            website_url: websiteUrl || undefined,
          });
        } else if (step === 1) {
          await patchProfile({
            working_hours: form.working_hours,
            buffer_between_jobs: form.buffer_between_jobs || undefined,
          });
        } else if (step === 2) {
          await patchProfile({
            pricing_notes: form.pricing_notes || undefined,
            callout_fee: form.callout_fee || undefined,
            hourly_rate: form.hourly_rate || undefined,
          });
        }
      } catch {}
      setSaving(false);
      setStep(step + 1);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const legacyDays = DAYS.filter(d => form.working_hours[d.key]?.enabled).map(d => d.label);
      const firstEnabled = Object.values(form.working_hours).find(d => d.enabled);
      await completeOnboarding({
        business_name: form.business_name,
        business_type: form.industry,
        services_offered: form.services_offered,
        working_days: legacyDays,
        workday_start_time: firstEnabled?.start || '08:00',
        workday_end_time: firstEnabled?.end || '17:00',
        working_hours: form.working_hours,
        buffer_between_jobs: form.buffer_between_jobs,
        buffer_minutes: form.buffer_between_jobs ? parseInt(form.buffer_between_jobs) : 15,
        default_job_duration_minutes: 60,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney',
        pricing_notes: form.pricing_notes,
        callout_fee: form.callout_fee,
        hourly_rate: form.hourly_rate,
        website_url: websiteUrl,
      });
      localStorage.setItem('mm_onboarding_done', 'true');
      navigate('/automations');
    } catch {}
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121820] flex items-center justify-center">
        <CircleNotch size={24} className="text-[#00E5C0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121820] flex items-center justify-center p-4">
      <div className="w-full max-w-xl" data-testid="business-setup-wizard">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-1 mb-8">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={s.id} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                    isDone ? 'bg-[#00E5C0] text-[#0A0E14]' :
                    isActive ? 'bg-[#00E5C0] text-[#0A0E14]' :
                    'bg-[rgba(255,255,255,0.06)] text-[#4B5563]'
                  }`}
                  data-testid={`setup-step-${s.id}`}
                >
                  {isDone ? <Check size={14} weight="bold" /> : <Icon size={14} />}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`w-8 h-0.5 mx-1 ${i < step ? 'bg-[#00E5C0]' : 'bg-[rgba(255,255,255,0.06)]'}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div className="bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-6 sm:p-8">

          {/* ─── Step 0: Business Profile ─── */}
          {step === 0 && (
            <div className="space-y-5" data-testid="setup-step-profile-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Tell us about your business</h2>
                <p className="text-sm text-[#7D8896]">Enter your website and we'll fill in the details for you, or type them manually.</p>
              </div>

              {/* Website URL + Autofill */}
              <div>
                <label className="text-xs font-medium text-[#B0B8C4] block mb-1.5">Website URL</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <GlobeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7D8896]" />
                    <input
                      type="url"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="www.yourbusiness.com"
                      data-testid="setup-website-url"
                      className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm placeholder:text-[#4B5563] focus:outline-none focus:border-[#00E5C0]"
                    />
                  </div>
                  <button
                    onClick={handleAutofill}
                    disabled={autofilling || !websiteUrl.trim()}
                    data-testid="setup-autofill-btn"
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-[rgba(0,229,192,0.08)] text-[#00E5C0] text-xs font-medium rounded-xl border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.15)] transition-all disabled:opacity-40 shrink-0"
                  >
                    {autofilling ? <CircleNotch size={13} className="animate-spin" /> : <MagicWand size={13} />}
                    Auto-fill my details
                  </button>
                </div>
              </div>

              {/* Autofill success banner */}
              {autofillResult && (
                <div className="bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.1)] rounded-xl px-4 py-3" data-testid="autofill-success">
                  <div className="flex items-center gap-2">
                    <Check size={14} className="text-[#00E5C0]" weight="bold" />
                    <p className="text-[11px] font-medium text-[#00E5C0]">Details extracted from your website. Review below and continue.</p>
                  </div>
                </div>
              )}

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
                <span className="text-[10px] text-[#4B5563] uppercase tracking-wider">or enter manually</span>
                <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
              </div>

              <InputField label="Business name" value={form.business_name} onChange={v => set('business_name', v)} placeholder="e.g. Smith Plumbing" testId="setup-business-name" />

              <div>
                <label className="text-xs font-medium text-[#B0B8C4] block mb-1.5">Industry</label>
                <select
                  value={form.industry}
                  onChange={e => set('industry', e.target.value)}
                  data-testid="setup-industry"
                  className="w-full px-3 py-2.5 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm focus:outline-none focus:border-[#00E5C0] [&>option]:bg-[#121820]"
                >
                  <option value="">Select your industry</option>
                  {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind.toLowerCase()}>{ind}</option>
                  ))}
                </select>
              </div>

              <InputField label="Services you offer" value={form.services_offered} onChange={v => set('services_offered', v)} placeholder="e.g. Emergency plumbing, hot water installs, bathroom renovations" rows={3} testId="setup-services" />
              <p className="text-[10px] text-[#4B5563] -mt-3">Separate services with commas. Your AI uses this to answer customer questions accurately.</p>
            </div>
          )}

          {/* ─── Step 1: Working Hours ─── */}
          {step === 1 && (
            <div className="space-y-5" data-testid="setup-step-hours-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">When do you usually work?</h2>
                <p className="text-sm text-[#7D8896]">We'll only schedule jobs during these hours.</p>
              </div>

              <div className="space-y-2">
                {DAYS.map(d => {
                  const day = form.working_hours[d.key] || { enabled: false, start: '08:00', end: '17:00' };
                  return (
                    <div key={d.key} className="flex items-center gap-3">
                      <button
                        onClick={() => toggleDay(d.key)}
                        data-testid={`setup-day-${d.key}`}
                        className={`w-10 h-10 rounded-lg text-xs font-semibold transition-all flex items-center justify-center ${
                          day.enabled
                            ? 'bg-[#00E5C0] text-[#0A0E14]'
                            : 'bg-[rgba(255,255,255,0.04)] text-[#4B5563] border border-[rgba(255,255,255,0.08)]'
                        }`}
                      >
                        {d.label}
                      </button>
                      {day.enabled && (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="time"
                            value={day.start}
                            onChange={e => setDayTime(d.key, 'start', e.target.value)}
                            className="px-2 py-1.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs focus:outline-none focus:border-[#00E5C0]"
                          />
                          <span className="text-[#4B5563] text-xs">to</span>
                          <input
                            type="time"
                            value={day.end}
                            onChange={e => setDayTime(d.key, 'end', e.target.value)}
                            className="px-2 py-1.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs focus:outline-none focus:border-[#00E5C0]"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <InputField label="Buffer between jobs (minutes, optional)" value={form.buffer_between_jobs} onChange={v => set('buffer_between_jobs', v)} placeholder="e.g. 15" testId="setup-buffer" type="number" />
            </div>
          )}

          {/* ─── Step 2: Pricing ─── */}
          {step === 2 && (
            <div className="space-y-5" data-testid="setup-step-pricing-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">How do you typically charge?</h2>
                <p className="text-sm text-[#7D8896]">This helps your AI give accurate estimates when customers ask about pricing.</p>
              </div>

              <InputField label="Pricing notes" value={form.pricing_notes} onChange={v => set('pricing_notes', v)} placeholder="e.g. Standard jobs start at $150. Hourly rate is $95/hr for labour plus materials." rows={3} testId="setup-pricing-notes" />
              <InputField label="Callout fee (optional)" value={form.callout_fee} onChange={v => set('callout_fee', v)} placeholder="e.g. $80" testId="setup-callout-fee" />
              <InputField label="Hourly rate (optional)" value={form.hourly_rate} onChange={v => set('hourly_rate', v)} placeholder="e.g. $95/hr" testId="setup-hourly-rate" />

              <p className="text-[10px] text-[#4B5563]">You can skip this and add pricing later in Settings.</p>
            </div>
          )}

          {/* ─── Step 3: Knowledge ─── */}
          {step === 3 && (
            <div className="space-y-5" data-testid="setup-step-knowledge-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Upload your knowledge base</h2>
                <p className="text-sm text-[#7D8896]">Upload documents your workers can reference — pricing sheets, service guides, FAQs, or anything else.</p>
              </div>

              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.csv,.md,.rtf"
                  onChange={handleUpload}
                  className="hidden"
                  data-testid="setup-knowledge-file-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="setup-knowledge-upload-btn"
                  className="flex items-center gap-2 px-4 py-2.5 bg-[rgba(0,229,192,0.06)] text-[#00E5C0] text-sm font-medium rounded-xl border border-[rgba(0,229,192,0.12)] hover:bg-[rgba(0,229,192,0.12)] transition-all disabled:opacity-50"
                >
                  {uploading ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />}
                  {uploading ? 'Uploading...' : 'Upload document'}
                </button>
                <p className="text-[10px] text-[#4B5563] mt-2">Supported: PDF, DOC, DOCX, TXT, CSV, MD. Max 10 MB.</p>
              </div>

              {documents.length > 0 ? (
                <div className="space-y-2" data-testid="setup-knowledge-list">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] rounded-xl px-4 py-3" data-testid={`setup-knowledge-doc-${doc.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-[rgba(0,229,192,0.08)] flex items-center justify-center shrink-0">
                          <FileIcon size={14} className="text-[#00E5C0]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-white font-medium truncate">{doc.filename}</p>
                          <p className="text-[10px] text-[#7D8896]">{formatSize(doc.size_bytes || 0)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteDoc(doc.id, doc.filename)}
                        className="p-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.05)] text-[#7D8896] hover:text-red-400 transition-colors shrink-0"
                        data-testid={`setup-delete-doc-${doc.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.06)] rounded-xl">
                  <BookOpen size={28} className="text-[#4B5563] mx-auto mb-2" />
                  <p className="text-xs text-[#4B5563]">No documents yet. Upload pricing sheets, service guides, or FAQs.</p>
                </div>
              )}

              <p className="text-[10px] text-[#4B5563]">You can always upload more documents later from Settings.</p>
            </div>
          )}

          {/* ─── Step 4: Done ─── */}
          {step === 4 && (
            <div className="text-center py-4" data-testid="setup-step-done-content">
              <div className="w-16 h-16 rounded-full bg-[rgba(0,229,192,0.1)] flex items-center justify-center mx-auto mb-4">
                <Sparkle size={28} className="text-[#00E5C0]" weight="fill" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Your AI is now tailored to your business</h2>
              <p className="text-sm text-[#7D8896] mb-6 max-w-sm mx-auto">
                Workers will use your real business name, services, pricing, and hours when responding to customers.
              </p>

              <div className="bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.08)] p-4 mb-6 text-left max-w-xs mx-auto">
                <p className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Your setup</p>
                <div className="space-y-1.5 text-xs text-[#B0B8C4]">
                  {form.business_name && <p><span className="text-[#4B5563]">Business:</span> {form.business_name}</p>}
                  {form.industry && <p><span className="text-[#4B5563]">Industry:</span> {form.industry}</p>}
                  {form.services_offered && <p><span className="text-[#4B5563]">Services:</span> {form.services_offered.substring(0, 60)}{form.services_offered.length > 60 ? '...' : ''}</p>}
                  <p><span className="text-[#4B5563]">Working days:</span> {DAYS.filter(d => form.working_hours[d.key]?.enabled).map(d => d.label).join(', ')}</p>
                  {documents.length > 0 && <p><span className="text-[#4B5563]">Knowledge:</span> {documents.length} document{documents.length !== 1 ? 's' : ''}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-[rgba(255,255,255,0.04)]">
            {step > 0 && step < 4 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-1 text-xs text-[#7D8896] hover:text-white transition-colors"
                data-testid="setup-back"
              >
                <ArrowLeft size={12} /> Back
              </button>
            ) : <div />}

            {step < 4 ? (
              <button
                onClick={handleNext}
                disabled={!canAdvance() || saving}
                data-testid="setup-next"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#00E5C0] text-[#0A0E14] text-sm font-semibold hover:bg-[#00CCB0] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? <CircleNotch size={14} className="animate-spin" /> : null}
                {step === 3 ? 'Finish setup' : 'Continue'}
                <ArrowRight size={14} />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={saving}
                data-testid="setup-complete"
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#00E5C0] text-[#0A0E14] text-sm font-semibold hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
              >
                {saving ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
                Go to your workers
              </button>
            )}
          </div>

          {/* Skip for steps 2-3 */}
          {(step === 2 || step === 3) && (
            <button
              onClick={() => setStep(step + 1)}
              className="w-full text-center text-[11px] text-[#4B5563] hover:text-[#7D8896] mt-3 transition-colors"
              data-testid="setup-skip"
            >
              Skip for now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
